// Listing modes for Due Date Control — Dashboard / Due Dates / Templates.
//
// Modes are ROUTES (PATTERNS.md BP-14 "Module weight"), not `useState`.
// Mirrored from src/ata/modes.ts (the approved exemplar).
//
//   <base>/dashboard  → DueDateDashboard (open due dates at a glance)
//   <base>/list       → DueDateList view="assigned" (per-person due dates)
//   <base>/templates  → DueDateList view="templates" (requirements with no assignee)
//
// What a "template" is, measured on the customer's data (2026-10-20): an active
// row with NO assignee — `assigned_to` 0/NULL and no active whiteboard_recipients
// row ("Passport", "First Aid", "WHMIS Course Cert"). Every other row is an
// assigned due date, a per-person instance of one of those requirements. The
// server decides which is which (GET /v6/duedate?view=assigned|templates).

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  list: "Due Dates",
  templates: "Templates",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85). Same keys as the label map; each read off what the mode renders.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Overdue and upcoming",
  list: "Assigned to people",
  templates: "Requirements with no assignee",
};

/** Aaron chose Dashboard as the default (2026-10-20). */
export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * `<module>.viewMode` — matches the shape used across converted modules.
 */
export const MODE_STORAGE_KEY = "duedate.viewMode";

/**
 * Is this a real mode segment?
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
  stored = migrateModeSegment(stored);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base is
 * read from the pathname, never written down: the module is mounted at
 * `duedate`, `due_date_control`, `due-date-control` and `whiteboard`
 * (App.tsx), and at the nav row's own `to` (BP-41).
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
